import { ref, computed } from "vue";

export type DropPosition = "before" | "after";

export interface ColumnDragOptions {
  onDragStart?: (fieldKey: string) => void;
  onDragEnd?: () => void;
  onDrop?: (fromKey: string, toKey: string, position: DropPosition) => void;
}

export interface DragState {
  fieldKey: string;
  element: HTMLElement | null;
}

export function useColumnDrag(options: ColumnDragOptions = {}) {
  const { onDragStart, onDragEnd, onDrop } = options;

  const isDragging = ref(false);
  const draggedField = ref<string | null>(null);
  const dropTarget = ref<string | null>(null);
  const dropPosition = ref<DropPosition>("after");
  const dragOverElement = ref<HTMLElement | null>(null);

  /**
   * Начало перетаскивания столбца
   */
  const handleDragStart = (event: DragEvent, fieldKey: string) => {
    if (!event.dataTransfer) return;

    isDragging.value = true;
    draggedField.value = fieldKey;

    // Настраиваем dataTransfer
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", fieldKey);

    // Устанавливаем изображение для перетаскивания
    if (event.target instanceof HTMLElement) {
      const rect = event.target.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        event.target,
        rect.width / 2,
        rect.height / 2,
      );
    }

    if (onDragStart) {
      onDragStart(fieldKey);
    }
  };

  /**
   * Обработка перетаскивания над элементом
   */
  const handleDragOver = (event: DragEvent, fieldKey: string) => {
    event.preventDefault();

    if (!event.dataTransfer) return;

    event.dataTransfer.dropEffect = "move";

    // Не позволяем drop на тот же элемент
    if (fieldKey === draggedField.value) {
      dropTarget.value = null;
      return;
    }

    dropTarget.value = fieldKey;

    // Определяем позицию drop (before или after)
    if (event.currentTarget instanceof HTMLElement) {
      const rect = event.currentTarget.getBoundingClientRect();
      const midX = rect.left + rect.width / 2;

      dropPosition.value = event.clientX < midX ? "before" : "after";
      dragOverElement.value = event.currentTarget as HTMLElement;
    }
  };

  /**
   * Обработка выхода из зоны drop
   */
  const handleDragLeave = (event: DragEvent) => {
    // Сбрасываем только если покинули элемент полностью
    if (event.target === dragOverElement.value) {
      dropTarget.value = null;
      dragOverElement.value = null;
    }
  };

  /**
   * Обработка drop
   */
  const handleDrop = (event: DragEvent, targetFieldKey: string) => {
    event.preventDefault();

    if (!draggedField.value || draggedField.value === targetFieldKey) {
      return;
    }

    if (onDrop) {
      onDrop(draggedField.value, targetFieldKey, dropPosition.value);
    }

    // Сбрасываем состояние
    resetState();
  };

  /**
   * Обработка окончания перетаскивания
   */
  const handleDragEnd = () => {
    if (onDragEnd) {
      onDragEnd();
    }

    resetState();
  };

  /**
   * Сброс состояния
   */
  const resetState = () => {
    isDragging.value = false;
    draggedField.value = null;
    dropTarget.value = null;
    dropPosition.value = "after";
    dragOverElement.value = null;
  };

  /**
   * Проверка, является ли поле перетаскиваемым
   */
  const isDraggedField = (fieldKey: string) => draggedField.value === fieldKey;

  /**
   * Проверка, является ли поле целью drop
   */
  const isDropTarget = (fieldKey: string) => dropTarget.value === fieldKey;

  /**
   * Получение позиции drop для отображения индикатора
   */
  const getDropIndicatorClass = (fieldKey: string) => {
    if (!isDropTarget(fieldKey)) return null;

    return dropPosition.value === "before"
      ? "drop-indicator-before"
      : "drop-indicator-after";
  };

  return {
    isDragging,
    draggedField,
    dropTarget,
    dropPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    isDraggedField,
    isDropTarget,
    getDropIndicatorClass,
  };
}

export type ColumnDrag = ReturnType<typeof useColumnDrag>;
